/* <sps-mark> — the Set Piece Supply rotating mark.
 *
 *   <script src="set-piece-mark.js"></script>
 *   <sps-mark size="40"></sps-mark>                        auto-rotates every 2400ms
 *   <sps-mark size="40" state="wall" cycle="0"></sps-mark>  static
 *   <sps-mark size="28" stroke="#fff" accent="#ff8a3d"></sps-mark>
 *
 * Attributes
 *   state   corner | wall | kickoff        which set piece to show (default corner)
 *   cycle   ms between states, 0 = off     (default 2400)
 *   size    px, the mark is square         (default 40)
 *   stroke  line colour                    (default oklch(0.19 0.012 60))
 *   accent  ball colour                    (default oklch(0.6 0.15 65))
 *   hover   "morph" to advance on hover instead of on a timer
 *
 * Respects prefers-reduced-motion: no auto-rotation, no transitions.
 * All attributes are live — set them any time and the mark morphs.
 */
(() => {
  const STATES = ['corner', 'wall', 'kickoff'];

  // Authored on a 120x120 grid, scaled to `size`. Every state uses the same
  // four parts, so each one only moves — nothing is added or removed.
  const FRAME = [
    { w: 120, h: 120, bw: [3, 3, 3, 3] },
    { w: 120, h: 120, bw: [0, 0, 3, 0] },
    { w: 120, h: 60,  bw: [0, 0, 3, 0] }
  ];
  const ARC = [
    { l: 0,  t: 0,  s: 72, bw: [0, 3, 3, 0], r: '0 0 100% 0', o: 1 },
    { l: 0,  t: 0,  s: 72, bw: [0, 0, 0, 0], r: '50%',        o: 0 },
    { l: 27, t: 27, s: 66, bw: [3, 3, 3, 3], r: '50%',        o: 1 }
  ];
  const BALL = [
    { l: 13,   t: 13, s: 12 },
    { l: 53.5, t: 28, s: 13 },
    { l: 54,   t: 54, s: 12 }
  ];
  const WALL_X = [9.5, 30.5, 51.5, 72.5, 93.5];
  const WALL = [
    () => ({ l: 21, t: 21, s: 0 }),
    (x) => ({ l: x,  t: 67, s: 17 }),
    () => ({ l: 60, t: 60, s: 0 })
  ];

  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  class SpsMark extends HTMLElement {
    static observedAttributes = ['state', 'cycle', 'size', 'stroke', 'accent', 'hover'];

    connectedCallback() {
      if (!this.shadowRoot) this.build();
      this.paint();
      this.schedule();
      if (this.getAttribute('hover') === 'morph') {
        this.addEventListener('mouseenter', this.advance);
        this.addEventListener('focus', this.advance);
      }
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.removeEventListener('mouseenter', this.advance);
      this.removeEventListener('focus', this.advance);
    }

    attributeChangedCallback() {
      if (this.shadowRoot) { this.paint(); this.schedule(); }
    }

    build() {
      const root = this.attachShadow({ mode: 'open' });
      root.innerHTML = `<style>
        :host { display: inline-block; line-height: 0; }
        .stage { position: relative; }
        .stage > * { position: absolute; box-sizing: border-box; border-style: solid; }
      </style><div class="stage">
        <div class="frame"></div><div class="arc"></div>
        <div class="dot"></div><div class="dot"></div><div class="dot"></div>
        <div class="dot"></div><div class="dot"></div>
        <div class="ball"></div>
      </div>`;
      this.stage = root.querySelector('.stage');
      this.els = {
        frame: root.querySelector('.frame'),
        arc: root.querySelector('.arc'),
        ball: root.querySelector('.ball'),
        dots: [...root.querySelectorAll('.dot')]
      };
    }

    get index() {
      const i = STATES.indexOf((this.getAttribute('state') || '').toLowerCase());
      return i < 0 ? 0 : i;
    }

    advance = () => {
      this.setAttribute('state', STATES[(this.index + 1) % STATES.length]);
    };

    schedule() {
      clearInterval(this.timer);
      const ms = Number(this.getAttribute('cycle') ?? 2400);
      if (!ms || reduced() || this.getAttribute('hover') === 'morph') return;
      this.timer = setInterval(this.advance, Math.max(600, ms));
    }

    paint() {
      const i = this.index;
      const size = Number(this.getAttribute('size') || 40);
      const k = size / 120;
      const stroke = this.getAttribute('stroke') || 'oklch(0.19 0.012 60)';
      const accent = this.getAttribute('accent') || 'oklch(0.6 0.15 65)';
      const ms = Number(this.getAttribute('cycle') ?? 2400);
      const dur = reduced() ? 0 : Math.max(320, Math.round((ms || 2400) * 0.38));
      const ease = `all ${dur}ms cubic-bezier(0.68, 0, 0.3, 1)`;
      const px = (n) => `${n * k}px`;

      Object.assign(this.stage.style, { width: px(120), height: px(120) });

      const f = FRAME[i];
      Object.assign(this.els.frame.style, {
        left: 0, top: 0, width: px(f.w), height: px(f.h),
        borderColor: stroke, borderWidth: f.bw.map((b) => px(b)).join(' '),
        transition: ease
      });

      const a = ARC[i];
      Object.assign(this.els.arc.style, {
        left: px(a.l), top: px(a.t), width: px(a.s), height: px(a.s), opacity: a.o,
        borderColor: stroke, borderWidth: a.bw.map((b) => px(b)).join(' '),
        borderRadius: a.r, transition: ease
      });

      this.els.dots.forEach((el, n) => {
        const d = WALL[i](WALL_X[n]);
        Object.assign(el.style, {
          left: px(d.l), top: px(d.t), width: px(d.s), height: px(d.s),
          borderRadius: '50%', borderWidth: 0, background: stroke,
          opacity: d.s ? 1 : 0, transition: ease
        });
      });

      const b = BALL[i];
      Object.assign(this.els.ball.style, {
        left: px(b.l), top: px(b.t), width: px(b.s), height: px(b.s),
        borderRadius: '50%', borderWidth: 0, background: accent, transition: ease
      });

      this.setAttribute('role', 'img');
      this.setAttribute('aria-label', 'Set Piece Supply');
    }
  }

  if (!customElements.get('sps-mark')) customElements.define('sps-mark', SpsMark);
})();
